import React from "react";
import PropTypes from "prop-types";
import Api from "js/api/authorisedApi";
import AlienNippleLoader from "js/components/AlienNippleLoader";
import { applyInputErrorStyling, removeInputErrorStyling } from "js/utils/dynamicStyling";
import PrivacyPolicy from "../components/PrivacyPolicy";
import LdapLoginButton from "./components/LdapLoginButton";
import ClasslinkLoginButton from "./components/ClasslinkLoginButton";
import GoogleLoginButton from "./components/GoogleLoginButton";
import AzureLoginButton from "./components/AzureLoginButton";
import { withRouter } from "js/components/Root/withRouter";
import { useThemeStore } from "stores/v2/themeStore";
import { datadogLogs } from "@datadog/browser-logs";
import { useUserSessionStore } from "stores/v2/userSessionStore";

class DeviceLogin extends React.Component {
	constructor(props) {
		super(props);

		this.state = {
			deviceid: localStorage.getItem("LOGIN_DEVICEID"),
			devices: [],
			redirecting: false,

			loginFailed: useUserSessionStore.getState().failedLogin(),
			loginFailedReason: useUserSessionStore.getState().failedLoginReason(),

			enabledLoginsLoaded: false,
			googleLoginEnabled: false,
			azureLoginEnabled: false,
			classlinkLoginEnabled: false,
			loadingOptions: true,
		};

		this.schoolSelectElement = null;
		this.usernameInputElement = null;
		this.passwordInputElement = null;

		this.unsubUserSessionStore = () => {};
	}

	componentDidMount() {
		Api.unauthenticatedGet("/get/device/from/tenant_key?tenant_key=" + this.props.router.params.tenant_key, (data) => {
			if (data?.data !== undefined) {
				this.setState({
					enabledLoginsLoaded: false,
					deviceid: data.data["device_id"],
				});
				if (data.data["device_id"] !== null) {
					this.fetchLoginMethods(data.data["device_id"]);
				} else {
					// Tenant Key was invalid so we just render a blank form for the user to fill out
					// but really the form wont do anything seeing as the tenant key was a FAKE
					this.setState({
						loadingOptions: false,
						enabledLoginsLoaded: true,
					});
				}
			} else {
				//Did not receive a response while searching for deviceID by Tenant Key, try again.
				//Ideally this should not happen.
				console.warn("Device Login - did not receive a response for searching device by tenant key");
				datadogLogs.logger.warn("Device Login - did not receive a response for searching device by tenant key");
			}
		});

		this.unsubUserSessionStore = useUserSessionStore.subscribe(this.onChange);
	}

	componentDidUpdate() {
		if (this.state.loginFailed) {
			this.setState({ ldapLoggingIn: false, loginFailed: false });
			applyInputErrorStyling(this.usernameInputElement, this.passwordInputElement);
		}
	}

	componentWillUnmount() {
		this.unsubUserSessionStore();
	}

	onChange = (state) => {
		if (useUserSessionStore.getState().isAuthenticated()) {
			this.props.router.navigate("/");
		}

		this.setState({
			loginFailed: useUserSessionStore.getState().failedLogin(),
			loginFailedReason: useUserSessionStore.getState().failedLoginReason(),
		});
	};

	handleLdapUsernameChange = () => {
		removeInputErrorStyling(this.usernameInputElement);
	};

	handleLdapPasswordChange = () => {
		removeInputErrorStyling(this.passwordInputElement);
	};

	ldapLoginValid = (username, password) => {
		// clear any previous error styling
		removeInputErrorStyling(this.usernameInputElement, this.passwordInputElement);

		const errorElements = [];
		if (username.length === 0) {
			errorElements.push(this.usernameInputElement);
		}
		if (password.length === 0) {
			errorElements.push(this.passwordInputElement);
		}
		applyInputErrorStyling(...errorElements);

		const validationPassed = errorElements.length === 0;
		return validationPassed;
	};

	handleLdapSubmit = (username, password) => {
		removeInputErrorStyling(this.usernameInputElement, this.passwordInputElement);

		if (this.ldapLoginValid(username, password)) {
			this.setState({ ldapLoggingIn: true });
			useUserSessionStore.getState().authenticate({
				deviceid: this.state.deviceid,
				username: username,
				password: password,
			});
		}
	};

	fetchLoginMethods = (deviceid) => {
		Api.get(
			"/backend/" + deviceid + "/authentication/options",
			(result) => {
				const loginOptions = result["result"];
				this.setState({
					enabledLoginsLoaded: true,
					googleLoginEnabled: loginOptions["google"],
					azureLoginEnabled: loginOptions["azure"],
					classlinkLoginEnabled: loginOptions["classlink"],
					loadingOptions: false,
				});
			},
			(err) => {
				this.setState({ enabledLoginsLoaded: false });
			}
		);
	};

	renderLoginFormOptions() {
		return (
			<div>
				{this.state.googleLoginEnabled && <GoogleLoginButton deviceId={this.state.deviceid} />}
				{this.state.azureLoginEnabled && <AzureLoginButton deviceId={this.state.deviceid} />}
				{this.state.classlinkLoginEnabled && <ClasslinkLoginButton deviceId={this.state.deviceid} />}
				{/* Render 'or' separator if any of the above third party sign in buttons are enabled */}
				{(this.state.googleLoginEnabled || this.state.azureLoginEnabled || this.state.classlinkLoginEnabled) && (
					<div className="login-separator">
						<div className="login-separator-line" />
						<div className="login-separator-text">or</div>
						<div className="login-separator-line" />
					</div>
				)}
				<LdapLoginButton
					deviceid={this.state.deviceid}
					usernameRef={(el) => (this.usernameInputElement = el)}
					passwordRef={(el) => (this.passwordInputElement = el)}
					handleSubmit={this.handleLdapSubmit}
					loginInProgress={this.state.ldapLoggingIn}
					onUsernameChange={this.handleLdapUsernameChange}
					onPasswordChange={this.handleLdapPasswordChange}
				/>
			</div>
		);
	}

	renderLoginStage = () => {
		if (this.state.loadingOptions) {
			return (
				<div className="login-schools-loading-container">
					<AlienNippleLoader />
					<span className="schools-loading-text">Loading school login. Please wait...</span>
					<button disabled className={"large login-next-button large-btn-disabled"}>
						NEXT
					</button>
				</div>
			);
		} else if (this.state.enabledLoginsLoaded) {
			return this.renderLoginFormOptions();
		}
	};

	render() {
		return (
			<div id="login-main-content">
				<div className="background">
					<div className="background-left" />
					<div className="background-right" />
				</div>
				<div className="flex-row-center">
					<div className="login-content">
						<div className={useThemeStore.getState().theme === "smoothwall" ? "login-logo-smoothwall" : "login-logo"} />
						{useThemeStore.getState().theme === "linewize" && <h1>Classwize</h1>}
						<h3>TEACHER PORTAL</h3>
						<div className="login-content-inner">{this.renderLoginStage()}</div>
					</div>
				</div>
				<PrivacyPolicy />
			</div>
		);
	}
}

DeviceLogin.propTypes = {
	params: PropTypes.object,
	router: PropTypes.object.isRequired,
};

export default withRouter(DeviceLogin);
